import clsx from "clsx";
import { points } from "../lib/format";

/**
 * Comparador **saliente → entrante** de la sección 8.4.
 *
 * QUÉ PROBLEMA RESUELVE
 *   Un movimiento del recomendador se resume en «+4,1». Eso hay que creérselo. Aquí cada métrica
 *   —puntos esperados, disponibilidad, eficiencia, tendencia— se parte en dos mitades de la misma
 *   barra: arriba, en gris, quien sale; abajo, en acento, quien entra. Y al lado la cifra exacta.
 *   Quien mire la pantalla ve **de dónde sale** la mejora, no solo cuánto vale.
 *
 * CÓMO SE ESCALA UNA BARRA
 *   Cada fila se normaliza contra su propio par de valores, con el cero siempre dentro del
 *   rango. Con métricas que pueden ser negativas —la tendencia— una escala que empezara en el
 *   mínimo haría que una caída del 4 % pareciera una barra vacía y una subida del 3 %, llena.
 *   Con el cero dentro, la mitad de la barra sigue significando algo.
 *
 * POR QUÉ NO BASTA EL COLOR
 *   Las dos mitades se distinguen por posición (arriba/abajo) y por la cifra escrita, no solo
 *   por el gris y el verde.
 */

export interface ComparadorRow {
  /** Nombre corto de la métrica: *Pts esperados*, *Disponibilidad*, *Eficiencia*, *Tendencia*. */
  label: string;
  /** Valor de quien sale. */
  out: number;
  /** Valor de quien entra. */
  in: number;
  /** Cómo se escribe la cifra exacta. Por defecto, puntos con una decimal. */
  format?: (valor: number) => string;
}

export interface ComparadorProps {
  rows: ComparadorRow[];
  /** Nombres de los dos jugadores, para la etiqueta accesible de cada fila. */
  outName?: string;
  inName?: string;
  className?: string;
}

/** Porcentaje de barra que ocupa `valor` en una escala que siempre contiene el cero. */
function anchura(valor: number, minimo: number, maximo: number): number {
  const recorrido = maximo - minimo;
  if (recorrido <= 0) return 0;
  return Math.min(100, Math.max(0, ((valor - minimo) / recorrido) * 100));
}

export function Comparador({
  rows,
  outName,
  inName,
  className,
}: ComparadorProps) {
  return (
    <div className={clsx("grid gap-[7px]", className)}>
      {rows.map((row) => {
        const escribir = row.format ?? points;
        const minimo = Math.min(0, row.out, row.in);
        const maximo = Math.max(0, row.out, row.in);
        const detalle = `${row.label}: ${outName ?? "sale"} ${escribir(row.out)}, ${inName ?? "entra"} ${escribir(row.in)}`;

        return (
          <div
            key={row.label}
            className="grid grid-cols-[76px_1fr_auto] items-center gap-2 text-[11px]"
          >
            <span className="min-w-0 truncate text-muted">{row.label}</span>
            <span
              role="img"
              aria-label={detalle}
              className="relative h-3.5 overflow-hidden rounded-full bg-white/[0.08]"
            >
              <i
                aria-hidden="true"
                className="absolute bottom-1/2 left-0 top-0 rounded-full bg-white/30"
                style={{ width: `${anchura(row.out, minimo, maximo)}%` }}
              />
              <i
                aria-hidden="true"
                className="absolute bottom-0 left-0 top-1/2 rounded-full bg-pitch"
                style={{ width: `${anchura(row.in, minimo, maximo)}%` }}
              />
            </span>
            <span className="whitespace-nowrap font-mono tabular-nums">
              {escribir(row.out)}
              <span className="text-muted">→</span>
              {escribir(row.in)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
